import type { GlobalAsset, Organization } from '@types';

const normalized = (value: unknown) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const address = (location: Record<string, unknown>) => [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.administrativeArea,
    location.postalCode,
    location.country,
].map(value => String(value || '').trim()).filter(Boolean).join(', ');

const addressParts = (location: Record<string, unknown>) => ({
    streetAddress: [location.addressLine1, location.addressLine2].map(value => String(value || '').trim()).filter(Boolean).join(', '),
    city: String(location.city || '').trim(),
    administrativeArea: String(location.administrativeArea || '').trim(),
    postalCode: String(location.postalCode || '').trim(),
    country: String(location.country || '').trim(),
});

const coordinates = (value: unknown) => {
    const input = value as Record<string, unknown> | null;
    const lat = Number(input?.latitude);
    const lng = Number(input?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const hash = (value: string) => {
    let result = 2166136261;
    for (let index = 0; index < value.length; index++) {
        result ^= value.charCodeAt(index);
        result = Math.imul(result, 16777619);
    }
    return (result >>> 0).toString(36);
};

export function organizationGlobalAssets(organization: Organization): GlobalAsset[] {
    const createdAt = Date.parse(organization.msaEffectiveAt || organization.createdAt || '') || 0;
    const headquartersAddress = organization.headquartersAddress || {} as NonNullable<Organization['headquartersAddress']>;
    const headquartersCoordinates = coordinates(organization.headquartersCoordinates);
    const headquarters: GlobalAsset = {
        id: `msa-${organization.id}-hq`,
        clientId: organization.id,
        name: `${organization.brandName || organization.name} Headquarters`,
        type: 'Facility',
        criticality: 'Medium',
        isPrimary: true,
        description: 'Headquarters managed on the management site.',
        createdAt,
        country: headquartersAddress.country,
        location: {
            lat: headquartersCoordinates?.lat || 0,
            lng: headquartersCoordinates?.lng || 0,
            address: address(headquartersAddress),
            addressParts: addressParts(headquartersAddress),
        },
    };
    const additional = (organization.additionalLocations || []).map((location, index): GlobalAsset => {
        const locationAddress = address(location);
        const locationCoordinates = coordinates(location.coordinates);
        return {
            id: `msa-${organization.id}-${hash(normalized(locationAddress) || String(index))}`,
            clientId: organization.id,
            name: [location.city, location.country].filter(Boolean).join(', ') || `Additional Location ${index + 1}`,
            type: 'Facility',
            criticality: 'Medium',
            description: 'Additional location managed on the management site.',
            createdAt,
            country: location.country,
            location: {
                lat: locationCoordinates?.lat || 0,
                lng: locationCoordinates?.lng || 0,
                address: locationAddress,
                addressParts: addressParts(location),
            },
        };
    });
    return address(headquartersAddress) ? [headquarters, ...additional] : additional;
}
